import { useEffect, useState } from 'react';

import { FarIcons } from '@playbooks/icons';
import { Button } from '@playbooks/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@playbooks/ui/components/popover';
import { StructuralIcon } from '@playbooks/ui/components/structural-icon';

import { ComposerActionsComposer } from 'components/composer/composer-actions-composer';
import { ComposerActionsConnectors } from 'components/composer/composer-actions-connectors';
import { ComposerActionsDelivery } from 'components/composer/composer-actions-delivery';
import { ComposerActionsDesign } from 'components/composer/composer-actions-design';
import { ComposerActionsMcps } from 'components/composer/composer-actions-mcps';
import { ComposerActionsPermissions } from 'components/composer/composer-actions-permissions';
import { ComposerActionsSelect } from 'components/composer/composer-actions-select';
import { ComposerActionsSkills } from 'components/composer/composer-actions-skills';
import { ComposerActionsTools } from 'components/composer/composer-actions-tools';
import { ComposerMemoryDialog } from 'components/composer/composer-memory-dialog';
import { useProject, useStore, useToast, useWorkspace } from 'contexts';
import { FlowFrame, FlowView } from 'utils/flow';

const deliveryLabels = { queue: 'Queue', steer: 'Steer' };
const permissionLabels = { autonomous: 'Autonomous', critical: 'Ask first', all: 'Ask always' };

const ComposerActions = ({
	type = 'app',
	disabled = false,
	canManagePreferences = false,
	canManageProjectDefaults = false,
	rootApi = undefined,
	onPreferencesChange = undefined,
	preferences = undefined,
	connectors = [],
	onDraftPreferencesChange = undefined,
	onDraftConnectorsChange = undefined,
	deliveryMode = 'queue',
	onDeliveryModeChange = undefined,
	modeDisabled = false,
	open = undefined,
	onOpenChange = undefined,
}) => {
	const [localOpen, setLocalOpen] = useState(false);
	const [memoryOpen, setMemoryOpen] = useState(false);
	const [view, setView] = useState('select');
	const [direction, setDirection] = useState('forward');
	const project = useProject();
	const store = useStore();
	const toast = useToast();
	const workspace = useWorkspace();
	const controlled = typeof open === 'boolean';
	const computedOpen = controlled ? open : localOpen;
	const nestedDisabled = type === 'app' ? false : !canManagePreferences;
	const scopedPreferences = type === 'project' ? project?.project?.preferences || {} : preferences || {};
	const permissions =
		scopedPreferences.permissions || (type === 'app' || type === 'project' ? 'autonomous' : 'critical');
	const memory = scopedPreferences.memory || '';
	const editable = type === 'app' || canManagePreferences;
	const composerSummary = `${deliveryLabels[deliveryMode] || 'Queue'} · ${permissionLabels[permissions] || 'Ask first'}`;
	const memoryLimit = type === 'project' ? 2000 : 12000;
	const memoryDescription =
		type === 'project'
			? 'Applied to this project alongside workspace memory.'
			: type === 'workspace'
				? 'Applied to every Workspace Operator run.'
				: type === 'agent'
					? 'Applied to every run by this agent.'
					: 'Applied to the agent or project created from this draft.';

	// Hooks
	useEffect(() => {
		if (computedOpen) {
			setView('select');
			setDirection('forward');
			return;
		}
	}, [computedOpen]);

	// Methods
	const onBack = () => {
		setDirection('backward');
		setView('select');
	};
	const onBackToComposer = () => {
		setDirection('backward');
		setView('composer');
	};
	const onView = nextView => {
		setDirection('forward');
		setView(nextView);
	};
	const setOpen = value => {
		if (!controlled) setLocalOpen(value);
		onOpenChange?.(value);
	};
	const onClose = () => setOpen(false);
	const onMemory = () => {
		onClose();
		setMemoryOpen(true);
	};
	const onDeliverySelect = async value => {
		if (!editable || modeDisabled || value === deliveryMode) return;
		try {
			if (type === 'agent') {
				const composerConfig = { ...(scopedPreferences.composerConfig || {}), deliveryMode: value };
				const response = await store.updateRecord({ url: `${rootApi}/preferences`, data: { composerConfig } });
				await onPreferencesChange?.(response.data);
			}
			onDeliveryModeChange?.(value);
		} catch (error) {
			toast.showError(error);
		}
	};
	const onPermissionsSelect = async value => {
		if (!editable || value === permissions) return;
		try {
			if (type === 'app') {
				onDraftPreferencesChange?.({ ...scopedPreferences, permissions: value });
				return;
			}
			if (type === 'workspace') {
				await store.saveRecord({
					url: `${workspace.rootApi}/preferences`,
					data: { permissions: value },
				});
				await onPreferencesChange?.();
				return;
			}
			if (type === 'agent') {
				const response = await store.updateRecord({ url: `${rootApi}/preferences`, data: { permissions: value } });
				await onPreferencesChange?.(response.data);
				return;
			}
			const response = await store.updateRecord({
				url: `${project.rootApi}/preferences`,
				data: { permissions: value },
			});
			project.onUpdate({ preferences: response.data });
			await onPreferencesChange?.(response.data);
		} catch (error) {
			toast.showError(error);
		}
	};
	const onMemorySave = async value => {
		try {
			if (type === 'app') {
				onDraftPreferencesChange?.({ ...scopedPreferences, memory: value });
				return;
			}
			if (type === 'workspace') {
				await store.saveRecord({ url: `${workspace.rootApi}/preferences`, data: { memory: value } });
				await onPreferencesChange?.();
				return;
			}
			if (type === 'agent') {
				const response = await store.updateRecord({ url: `${rootApi}/preferences`, data: { memory: value } });
				await onPreferencesChange?.(response.data);
				return;
			}
			const response = await store.updateRecord({ url: `${project.rootApi}/preferences`, data: { memory: value } });
			project.onUpdate({ preferences: response.data });
			await onPreferencesChange?.(response.data);
		} catch (error) {
			toast.showError(error);
			throw error;
		}
	};

	// Render
	return (
		<>
			<Popover open={computedOpen} onOpenChange={setOpen}>
				<PopoverTrigger
					render={
						<Button
							variant='outline'
							size='icon-xs'
							data-active={computedOpen}
							aria-label='Composer actions'
							disabled={disabled}>
							<StructuralIcon icon={FarIcons.farPlus} aria-hidden />
						</Button>
					}
				/>
				<PopoverContent
					align='start'
					side='bottom'
					sideOffset={8}
					data-composer-type={type}
					className='max-h-(--available-height) w-80 overflow-hidden p-0'>
					<FlowFrame layout='panel'>
						<FlowView key={view} direction={direction}>
							{view === 'select' && (
								<ComposerActionsSelect
									type={type}
									disabled={disabled}
									composerSummary={composerSummary}
									onView={onView}
								/>
							)}
							{view === 'composer' && (
								<ComposerActionsComposer
									delivery={deliveryLabels[deliveryMode] || 'Queue'}
									permissions={permissionLabels[permissions] || 'Ask first'}
									canManage={editable}
									onMemory={onMemory}
									onPrev={onBack}
									onView={onView}
								/>
							)}
							{view === 'delivery' && (
								<ComposerActionsDelivery
									disabled={!editable || modeDisabled}
									mode={deliveryMode}
									onChange={onDeliverySelect}
									onPrev={onBackToComposer}
								/>
							)}
							{view === 'permissions' && (
								<ComposerActionsPermissions
									disabled={!editable}
									permissions={permissions}
									onChange={onPermissionsSelect}
									onPrev={onBackToComposer}
								/>
							)}
							{view === 'design' && (
								<ComposerActionsDesign
									type={type}
									disabled={nestedDisabled}
									preferences={preferences}
									onChange={onDraftPreferencesChange}
									onPrev={onBack}
								/>
							)}
							{view === 'connectors' && (
								<ComposerActionsConnectors
									type={type}
									selected={connectors}
									onChange={onDraftConnectorsChange}
									onPrev={onBack}
								/>
							)}
							{view === 'skills' && (
								<ComposerActionsSkills
									type={type}
									disabled={nestedDisabled}
									preferences={preferences}
									onChange={onDraftPreferencesChange}
									onNext={onPreferencesChange}
									onPrev={onBack}
								/>
							)}
							{view === 'mcps' && (
								<ComposerActionsMcps
									type={type}
									disabled={nestedDisabled}
									preferences={preferences}
									canManageServers={canManageProjectDefaults}
									onChange={onDraftPreferencesChange}
									onNext={onPreferencesChange}
									onPrev={onBack}
								/>
							)}
							{view === 'tools' && (
								<ComposerActionsTools
									type={type}
									disabled={disabled}
									canManagePreferences={canManagePreferences}
									canManageProjectDefaults={canManageProjectDefaults}
									preferences={preferences}
									onChange={onDraftPreferencesChange}
									onNext={onPreferencesChange}
									onPrev={onBack}
								/>
							)}
						</FlowView>
					</FlowFrame>
				</PopoverContent>
			</Popover>
			<ComposerMemoryDialog
				open={memoryOpen}
				setOpen={setMemoryOpen}
				value={memory}
				canManage={editable}
				limit={memoryLimit}
				description={memoryDescription}
				onSave={onMemorySave}
			/>
		</>
	);
};
export { ComposerActions };
